import { Check, ChevronDown, Store } from 'lucide-react'
import { useRouterState } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import { useLocationStore } from '@/shared/store/location-store'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

/** Routes whose page makes its own, incompatible location choice (see the guard below). */
const SELF_SCOPED_ROUTES = ['/reports/accounting']

/**
 * The one location control (CHG-2026-09-manager-reporting, D16). Lives in the app header, applies
 * to the dashboard and every report, and survives navigating between them.
 *
 * It picks **several** locations, not one (CHG-2026-09-article-location-filter, D3): "wie laufen
 * die beiden neuen Standorte" is a question about a subset, and the old one-or-all switch could
 * only ask it about one store at a time.
 *
 * Ticking every location collapses back to "Alle Standorte" rather than storing three ids. The two
 * states select the same sales today, but only the empty one keeps counting a store that opens
 * next month — and two controls that look different while meaning the same thing invite the reader
 * to look for a difference that is not there.
 *
 * The internal staff store is not offered: it must appear in no revenue figure. That is enforced in
 * SQL as well — this is the second line of defence, not the mechanism.
 *
 * Ids that no longer resolve (a store closed, a different tenant) are dropped on read rather than
 * silently filtering the reports to a location that is not in the list.
 */
export function LocationSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation('reports')
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const stored = useLocationStore((s) => s.restaurantIds)
  const setRestaurantIds = useLocationStore((s) => s.setRestaurantIds)
  const { data } = useRestaurants()

  // `visibility` is optional on the DTO, so absent means PUBLIC.
  const locations = (data?.items ?? []).filter(
    (r) => r.visibility !== 'INTERNAL',
  )

  const selected = stored.filter((id) => locations.some((r) => r.id === id))
  const all = selected.length === 0

  function toggle(id: number) {
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id]
    setRestaurantIds(next.length === locations.length ? [] : next)
  }

  const label = all
    ? t('location.all')
    : selected.length === 1
      ? (locations.find((r) => r.id === selected[0])?.name ?? t('location.all'))
      : t('location.count', { value: selected.length })

  // Hidden where the page owns a *different* location selection, so the chrome cannot contradict
  // what is on screen: the Buchhaltung report files per single Standort — the staff store included —
  // and showed "Alle Standorte" up here next to its own picker naming one store. The switcher itself
  // is unchanged; only this one route hides it, and the stored selection still seeds that page.
  if (SELF_SCOPED_ROUTES.includes(pathname)) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-label={t('location.label')}
          className={cn(
            'h-12 w-full justify-start gap-2 font-normal sm:h-9 sm:w-56',
            className,
          )}
        >
          <Store
            className="h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <span className="truncate">{label}</span>
          <ChevronDown
            className="ml-auto h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1">
        <LocationOption
          label={t('location.all')}
          checked={all}
          onSelect={() => setRestaurantIds([])}
        />
        <div className="my-1 h-px bg-border" />
        {locations.map((r) => (
          <LocationOption
            key={r.id}
            label={r.name}
            checked={selected.includes(r.id)}
            onSelect={() => toggle(r.id)}
          />
        ))}
      </PopoverContent>
    </Popover>
  )
}

function LocationOption({
  label,
  checked,
  onSelect,
}: {
  label: string
  checked: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      onClick={onSelect}
      className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-muted sm:py-1.5"
    >
      <Check
        className={cn('h-4 w-4 shrink-0', !checked && 'invisible')}
        aria-hidden="true"
      />
      <span className="truncate">{label}</span>
    </button>
  )
}
